import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    Box, 
    Typography, 
    TextField, 
    Button,  
    CircularProgress, 
    FormControlLabel, 
    Checkbox, 
} from '@mui/material';
import {
    // Add,
    // Delete,
    ArrowBackIosNew,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import Terminal from './Terminal';


const AirportPage = () => {
    useAuthCheck();
    const { airportId } = useParams();    
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [airport, setAirport] = useState({});

    const initialValues = {
        iataCode: airport.iataCode || '',
        icaoCode: airport.icaoCode || '',
        airportName: airport.airportName || '',
        countryCode: airport.countryCode || '',
        isMainBase: airport.isMainBase || false,
        locationType: airport.locationType || 0,
        longitude: airport.longitude || '',
        latitude: airport.latitude || '',
        altitude: airport.altitude || 0,
        timeZone: airport.timeZone || '',
        timeZoneDiff: airport.timeZoneDiff || 0,
        ckNotEFB: airport.ckNotEFB || false,
        headInfo: airport.headInfo || '',
        heilideckHeight: airport.heilideckHeight || 0,
        isPossibleDiversionBit: airport.isPossibleDiversionBit || true,
        isPossibleFuelLocation: airport.isPossibleFuelLocation || true,
        magneticVariation: airport.magneticVariation || '',
        runwayLength: airport.runwayLength || 0,
        taxiDep: airport.taxiDep || 0,
        taxiArr: airport.taxiArr || 0,
        travelZone: airport.travelZone || '',
        terminals: airport.terminals || [],
    };
            
    useEffect(() => {        
        const fetchAirport = async () => {
            try {
                setLoading(true);
        
                if (airportId) {
                    const response = await api.get(`/airports/${airportId}`);        
                    setAirport(response.data); 
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchAirport();
    }, [airportId]);
    
    const checkoutSchema = yup.object().shape({
        iataCode: yup.string().required('Required'),
        airportName: yup.string().required('Required'),
        terminals: yup.array().of(
            yup.object({
                type: yup.string().oneOf(["Domestic", "International"]).required(),
                name: yup.string().nullable(),
                airportPickupPoint: yup.object({
                    name: yup.string().required('Pickup point required.'),
                }),
                airportDropoffPoint: yup.object({
                    name: yup.string().required('Dropoff point required.'),
                }),
            })
        )
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = airportId ? "PUT" : "POST";
            const url = airportId
                ? `/airports/update/${airportId}`
                : '/airports/new';
        
            const payload = {
                ...values,
                terminals: values.terminals,
            };
            
            await api({
                method,
                url,
                data: payload,
            });
    
            toast.success(airportId
                ? 'Airport updated successfully.'
                : 'Airport created successfully.'
            );
    
            onSubmitProps.resetForm();
            navigate('/airports');
        } catch (error) {
            setError(error);
            toast.error(error?.message || 'Failed to submit the form!');
        }
    };

    
    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Airport"
                    subtitle={`${loading ? 'Loading...' : airportId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/airports")}
                        >
                            <ArrowBackIosNew />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </Box>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={initialValues}
                        enableReinitialize={true}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            setFieldValue,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gap={2}
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                >
                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="text"
                                        label="IATA Code"
                                        name="iataCode"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.iataCode}
                                        error={!!touched.iataCode && !!errors.iataCode}
                                        helperText={touched.iataCode && errors.iataCode}
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="ICAO Code"
                                        name="icaoCode"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.icaoCode}
                                        error={!!touched.icaoCode && !!errors.icaoCode}
                                        helperText={touched.icaoCode && errors.icaoCode}
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                                    />

                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="text"
                                        label="Airport Name"
                                        name="airportName"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.airportName}
                                        error={!!touched.airportName && !!errors.airportName}
                                        helperText={touched.airportName && errors.airportName}
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Country Code"
                                        name="countryCode"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.countryCode}
                                        error={!!touched.countryCode && !!errors.countryCode}
                                        helperText={touched.countryCode && errors.countryCode}
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Location Type"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.locationType}
                                        name="locationType"
                                        error={!!touched.locationType && !!errors.locationType}
                                        helperText={touched.locationType && errors.locationType}
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                                    />
                                    
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="number"
                                        label="Latitude"
                                        name="latitude"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.latitude}       
                                        error={!!touched.latitude && !!errors.latitude}
                                        helperText={touched.latitude && errors.latitude}                                     
                                        slotProps={{
                                            input: {
                                                sx: {
                                                    "& input": { textAlign: "right" },
                                                },
                                            },
                                        }}
                                        sx={{ gridColumn: { xs: "span 12", sm: "span 3" } }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="number"
                                        label="Longitude"
                                        name="longitude"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.longitude}       
                                        error={!!touched.longitude && !!errors.longitude}
                                        helperText={touched.longitude && errors.longitude}                                     
                                        slotProps={{
                                            input: {
                                                sx: {
                                                    "& input": { textAlign: "right" },
                                                },
                                            },
                                        }}
                                        sx={{ gridColumn: { xs: "span 12", sm: "span 3" } }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="number"
                                        label="Altitude"
                                        name="altitude"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.altitude}       
                                        error={!!touched.altitude && !!errors.altitude}
                                        helperText={touched.altitude && errors.altitude}                                     
                                        slotProps={{
                                            input: {
                                                sx: {
                                                    "& input": { textAlign: "right" },
                                                },
                                            },
                                        }}
                                        sx={{ gridColumn: { xs: "span 12", sm: "span 3" } }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Time Zone"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.timeZone}
                                        name="timeZone"
                                        error={!!touched.timeZone && !!errors.timeZone}
                                        helperText={touched.timeZone && errors.timeZone}
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                                    />
                                    
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="number"
                                        label="Time Zone Diff"
                                        name="timeZoneDiff"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.timeZoneDiff}       
                                        error={!!touched.timeZoneDiff && !!errors.timeZoneDiff}
                                        helperText={touched.timeZoneDiff && errors.timeZoneDiff}                                     
                                        slotProps={{
                                            input: {
                                                sx: {
                                                    "& input": { textAlign: "right" },
                                                },
                                            },
                                        }}
                                        sx={{ gridColumn: { xs: "span 12", sm: "span 3" } }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Head Info"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.headInfo}
                                        name="headInfo"
                                        error={!!touched.headInfo && !!errors.headInfo}
                                        helperText={touched.headInfo && errors.headInfo}
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                                    />
                                    
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Magnetic Variation"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.magneticVariation}
                                        name="magneticVariation"
                                        error={!!touched.magneticVariation && !!errors.magneticVariation}
                                        helperText={touched.magneticVariation && errors.magneticVariation}
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                                    />
                                    
                                   <TextField
                                        fullWidth
                                        variant="filled"
                                        type="number"
                                        label="Heilideck Height"
                                        name="heilideckHeight"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.heilideckHeight}       
                                        error={!!touched.heilideckHeight && !!errors.heilideckHeight}
                                        helperText={touched.heilideckHeight && errors.heilideckHeight}                                     
                                        slotProps={{
                                            input: {
                                                sx: {
                                                    "& input": { textAlign: "right" },
                                                },
                                            },
                                        }}
                                        sx={{ gridColumn: { xs: "span 12", sm: "span 3" } }}
                                    />

                                   <TextField
                                        fullWidth
                                        variant="filled"
                                        type="number"
                                        label="Runway Length"
                                        name="runwayLength"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.runwayLength}       
                                        error={!!touched.runwayLength && !!errors.runwayLength}
                                        helperText={touched.runwayLength && errors.runwayLength}                                     
                                        slotProps={{
                                            input: {
                                                sx: {
                                                    "& input": { textAlign: "right" },
                                                },
                                            },
                                        }}
                                        sx={{ gridColumn: { xs: "span 12", sm: "span 3" } }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="number"
                                        label="Taxi Dep"
                                        name="taxiDep"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.taxiDep}       
                                        error={!!touched.taxiDep && !!errors.taxiDep}
                                        helperText={touched.taxiDep && errors.taxiDep}                                     
                                        slotProps={{
                                            input: {
                                                sx: {
                                                    "& input": { textAlign: "right" },
                                                },
                                            },
                                        }}
                                        sx={{ gridColumn: { xs: "span 12", sm: "span 3" } }}
                                    />
                                    
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="number"
                                        label="Taxi Arr"
                                        name="taxiArr"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.taxiArr}       
                                        error={!!touched.taxiArr && !!errors.taxiArr}
                                        helperText={touched.taxiArr && errors.taxiArr}                                     
                                        slotProps={{
                                            input: {
                                                sx: {
                                                    "& input": { textAlign: "right" },
                                                },
                                            },
                                        }}
                                        sx={{ gridColumn: { xs: "span 12", sm: "span 3" } }}
                                    />                                    
                                                                        
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        label="Travel Zone"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.travelZone}
                                        name="travelZone"
                                        error={!!touched.travelZone && !!errors.travelZone}
                                        helperText={touched.travelZone && errors.travelZone}
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                                    />
                                    
                                    <FormControlLabel
                                        label="Is MainBase"
                                        control={
                                            <Checkbox
                                                name="isMainBase"
                                                color="primary"
                                                checked={values.isMainBase}
                                                onChange={handleChange}
                                            />
                                        }
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                                    />
                                    
                                    <FormControlLabel
                                        label="CkNotEFB"
                                        control={
                                            <Checkbox
                                                name="ckNotEFB"
                                                color="primary"
                                                checked={values.ckNotEFB}
                                                onChange={handleChange}
                                            />
                                        }
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                                    />
                                    
                                    <FormControlLabel
                                        label="Is Possible Diversion Bit"
                                        control={
                                            <Checkbox
                                                name="isPossibleDiversionBit"
                                                color="primary"
                                                checked={values.isPossibleDiversionBit}
                                                onChange={handleChange}
                                            />
                                        }
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                                    />
                                    
                                    <FormControlLabel
                                        label="Is Possible Fuel Location"
                                        control={
                                            <Checkbox
                                                name="isPossibleFuelLocation"
                                                color="primary"
                                                checked={values.isPossibleFuelLocation}
                                                onChange={handleChange}
                                            />
                                        }
                                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                                    />
                                    
                                    <Box 
                                        display="flex" 
                                        flexDirection="column" 
                                        gap={1}
                                        sx={{ gridColumn: 'span 12' }}
                                    >
                                        <Typography variant="h5">Terminals & Transport</Typography>

                                        {values.terminals.map((terminal, idx) => (
                                            <Terminal
                                                key={idx}
                                                terminal={terminal}
                                                index={idx}
                                                setFieldValue={setFieldValue}
                                                values={values}
                                            />
                                        ))}
                                        
                                        <Button
                                            disabled={values.terminals.length >= 2}
                                            onClick={() =>
                                                setFieldValue('terminals', [
                                                ...values.terminals,
                                                {
                                                    type: '',
                                                    name: '',
                                                    airportPickupPoint: {},
                                                    airportDropoffPoint: {},
                                                },
                                                ])
                                            }
                                        >
                                            + Add Terminal
                                        </Button>
                                    </Box>

                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {airportId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default AirportPage;
